import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { AffiliateCommission, AffiliateCommissionStatus } from '../services/api';

interface AffiliateCommissionViewModalProps {
  visible: boolean;
  commission: AffiliateCommission | null;
  onCancel: () => void;
}

const AffiliateCommissionViewModal: React.FC<AffiliateCommissionViewModalProps> = ({
  visible,
  commission,
  onCancel,
}) => {
  if (!visible || !commission) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusColor = (status: AffiliateCommissionStatus) => {
    switch (status) {
      case AffiliateCommissionStatus.PENDING:
        return 'bg-yellow-100 text-yellow-800';
      case AffiliateCommissionStatus.CONFIRMED:
        return 'bg-blue-100 text-blue-800';
      case AffiliateCommissionStatus.PAID:
        return 'bg-green-100 text-green-800';
      case AffiliateCommissionStatus.VOID:
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Affiliate Commission Details</h2>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">ID</label>
                <p className="text-sm text-gray-900 mt-1">{commission.id}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Status</label>
                <p className="mt-1">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(commission.status)}`}>
                    {commission.status}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Commission Details */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Commission Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Commission Amount</label>
                <p className="text-sm text-gray-900 mt-1 font-semibold text-lg">
                  {formatCurrency(commission.commissionAmount)}
                </p>
              </div>
            </div>
          </div>

          {/* Related Entities */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Related Entities</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Affiliate Member</label>
                <p className="text-sm text-gray-900 mt-1">
                  {commission.affiliateMember?.code || `Member ID: ${commission.affiliateMemberId}`}
                </p>
                {commission.affiliateMember?.userId && (
                  <p className="text-xs text-gray-500 mt-1">User ID: {commission.affiliateMember.userId}</p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Program</label>
                <p className="text-sm text-gray-900 mt-1">
                  {commission.program?.name || `Program ID: ${commission.programId}`}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Order</label>
                <p className="text-sm text-gray-900 mt-1 font-mono">
                  {commission.order?.orderNumber || `Order ID: ${commission.orderId}`}
                </p>
                {commission.order && (
                  <p className="text-xs text-gray-500 mt-1">
                    Total: {formatCurrency(commission.order.totalAmount)}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Dates */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Dates</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Created At</label>
                <p className="text-sm text-gray-900 mt-1">{formatDate(commission.createdAt)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Confirmed At</label>
                <p className="text-sm text-gray-900 mt-1">{formatDate(commission.confirmedAt)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Paid At</label>
                <p className="text-sm text-gray-900 mt-1">{formatDate(commission.paidAt)}</p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end pt-4 border-t border-gray-200">
            <button
              onClick={onCancel}
              className="px-4 py-2 bg-imola-primary text-white rounded-md hover:bg-imola-primary/80"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AffiliateCommissionViewModal;

